'use client';

import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from '@/i18n/navigation';
import { useBookingStore } from '@/stores/booking-store';
import { SelectionOverview } from './selection-overview';
import { formatShortDate } from '@/lib/calendar-utils';
import { IoBedOutline } from 'react-icons/io5';
import { ChevronUp } from 'lucide-react';

interface RoomPrice {
  roomSlug: string;
  totalPrice: number;
  currency: string;
  available: boolean;
}

export function SelectionBar() {
  const t = useTranslations('selection');
  const router = useRouter();
  const pathname = usePathname();
  const { items, checkIn, checkOut, singleRoomCheckout, clearSelection } = useBookingStore();
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [prices, setPrices] = useState<Record<string, RoomPrice>>({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const fetchController = useRef<AbortController | null>(null);

  // Prevent hydration mismatch (sessionStorage not available during SSR)
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const hasItems = items.length > 0;
  const hasDates = !!(checkIn && checkOut);

  // Prevent overview from opening immediately when bar (re-)appears.
  // Covers first selection AND clear-then-reselect scenarios.
  // useLayoutEffect sets the timestamp before the browser paints / fires events.
  const barAppearTime = useRef(0);
  const prevHasItems = useRef(false);
  useLayoutEffect(() => {
    if (hasItems && !prevHasItems.current) {
      barAppearTime.current = Date.now();
    }
    prevHasItems.current = hasItems;
  }, [hasItems]);

  const isBarCooldown = () => Date.now() - barAppearTime.current < 600;

  // Fetch prices for all selected rooms when selection or dates change
  const fetchPrices = useCallback(async () => {
    if (!hasDates || items.length === 0) {
      setPrices({});
      return;
    }

    fetchController.current?.abort();
    const controller = new AbortController();
    fetchController.current = controller;

    setLoadingPrices(true);
    try {
      const res = await fetch(
        `/api/checkout/rooms?checkIn=${encodeURIComponent(checkIn!)}&checkOut=${encodeURIComponent(checkOut!)}`,
        { signal: controller.signal }
      );
      const result = await res.json();
      if (result.success && result.rooms) {
        const priceMap: Record<string, RoomPrice> = {};
        for (const room of result.rooms) {
          priceMap[room.slug] = {
            roomSlug: room.slug,
            totalPrice: room.totalPrice || 0,
            currency: room.currency || 'EUR',
            available: room.available,
          };
        }
        setPrices(priceMap);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
    } finally {
      setLoadingPrices(false);
    }
  }, [items.length, checkIn, checkOut, hasDates]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  // Hide on checkout page
  const isCheckout = pathname?.startsWith('/checkout');

  // Track whether the user has visited the checkout page during this single-room flow.
  // Only clear the store on BACK-navigation (was on checkout, now isn't), not on
  // forward navigation (singleRoomCheckout just set, haven't reached checkout yet).
  const wasOnCheckout = useRef(false);
  useEffect(() => {
    if (isCheckout && singleRoomCheckout) {
      wasOnCheckout.current = true;
    }
  }, [isCheckout, singleRoomCheckout]);

  // Clean up single-room checkout state on back-navigation.
  // Clears room items + flag but preserves dates so the calendar restores them.
  useEffect(() => {
    if (mounted && singleRoomCheckout && hasItems && !isCheckout && wasOnCheckout.current) {
      wasOnCheckout.current = false;
      clearSelection();
    }
  }, [mounted, singleRoomCheckout, hasItems, isCheckout, clearSelection]);

  // Expose the bar's height as a CSS var so flow content (body padding) and
  // other fixed bottom elements can offset themselves without overlapping.
  const isVisible = mounted && hasItems && !isCheckout && !singleRoomCheckout;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--selection-bar-height', isVisible ? '48px' : '0px');
    return () => root.style.setProperty('--selection-bar-height', '0px');
  }, [isVisible]);

  // Don't render for single-room checkout (prevents flash during navigation)
  // or on checkout page, or when store is empty
  if (!isVisible) return null;

  // Calculate totals (only available rooms contribute to the total)
  const selectedPrices = items
    .map((item) => prices[item.roomSlug])
    .filter((p): p is RoomPrice => !!p && p.available);
  const totalPrice = selectedPrices.reduce((sum, p) => sum + p.totalPrice, 0);
  const currency = selectedPrices[0]?.currency || 'EUR';
  const currencySymbol = currency === 'EUR' ? '€' : currency;
  const hasPricesLoaded = !loadingPrices && Object.keys(prices).length > 0;
  const priceReady = hasPricesLoaded;

  const handleProceed = () => {
    router.push('/checkout');
  };

  return (
    <>
      {/* Fixed bottom bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[55] transition-transform duration-300"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="bg-[#4e3529]/95 backdrop-blur-md border-t border-[#4e3529]">
          {/* Mobile: compact bar */}
          <div className="md:hidden">
            <button
              onClick={() => { if (!isBarCooldown()) setOverviewOpen(true); }}
              className="w-full flex items-center justify-between px-4 py-3 cursor-pointer"
            >
              <div className="flex items-center gap-2.5">
                <IoBedOutline className="w-5 h-5 text-white/80" />
                <span className="text-white font-semibold text-sm">
                  {t('roomsCount', { count: items.length })}
                </span>
                {hasDates && (
                  <>
                    <span className="text-white/50">·</span>
                    {priceReady ? (
                      <span className="text-white font-bold text-sm">{currencySymbol}{Math.round(totalPrice)} {t('total')}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-sm">
                        <span className="inline-block w-10 h-3.5 bg-white/20 rounded animate-pulse" />
                        <span className="text-white/50">{t('total')}</span>
                      </span>
                    )}
                  </>
                )}
              </div>
              <ChevronUp className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Desktop: wider bar with inline detail */}
          <div className="hidden md:flex items-center justify-between px-6 lg:px-10 py-3">
            <button
              onClick={() => { if (!isBarCooldown()) setOverviewOpen(true); }}
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <IoBedOutline className="w-5 h-5 text-white/80" />
              <span className="text-white font-semibold text-sm">
                {t('roomsCount', { count: items.length })}
              </span>
              {hasDates && (
                <>
                  <span className="text-white/40">·</span>
                  <span className="text-white/70 text-sm">
                    {formatShortDate(checkIn!)} — {formatShortDate(checkOut!)}
                  </span>
                  <span className="text-white/40">·</span>
                  {priceReady ? (
                    <span className="text-white font-bold text-sm">{currencySymbol}{Math.round(totalPrice)} {t('total')}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      <span className="inline-block w-12 h-3.5 bg-white/20 rounded animate-pulse" />
                      <span className="text-white/50">{t('total')}</span>
                    </span>
                  )}
                </>
              )}
              <ChevronUp className="w-4 h-4 text-white/50 ml-1" />
            </button>
            <button
              onClick={handleProceed}
              className="px-6 py-2.5 bg-white text-[#4e3529] font-bold text-sm uppercase tracking-wider rounded-full hover:bg-white/90 transition-colors cursor-pointer"
            >
              {t('proceedToCheckout')}
            </button>
          </div>
        </div>
      </div>

      {/* Selection overview modal/sheet */}
      {overviewOpen && (
        <SelectionOverview
          prices={prices}
          loadingPrices={loadingPrices}
          onClose={() => setOverviewOpen(false)}
          onProceed={handleProceed}
        />
      )}
    </>
  );
}
